import isEqual from 'lodash-es/isEqual';
import type { StickyModelContentsJSON } from '@view-model/domain/model/model-contents/StickyModelContents';
import type {
  StickyNodeJSON,
  LinkJSON,
  StickyZoneJSON,
  DescriptionPanelJSON,
  ElementDescriptionJSON,
} from 'balus-common';
import type { PositionSetJSON } from '@view-model/models/common/PositionSet';

/**
 * 2つのStickyModelContentsのダンプJSONを比較した結果。
 * 各コレクションについて「書き込むデータ」と「削除するID」を保持する。
 */
export type ModelContentsDiff = {
  nodes: { save: Record<string, StickyNodeJSON>; removeIds: string[] };
  links: { save: Record<string, LinkJSON>; removeIds: string[] };
  zones: { save: Record<string, StickyZoneJSON>; removeIds: string[] };
  zonePositions: { save: PositionSetJSON; removeIds: string[] };
  descriptionPanels: { save: Record<string, DescriptionPanelJSON>; removeIds: string[] };
  descriptionPanelPositions: { save: PositionSetJSON; removeIds: string[] };
  nodeDescriptions: { save: Record<string, ElementDescriptionJSON>; removeIds: string[] };
  zoneDescriptions: { save: Record<string, ElementDescriptionJSON>; removeIds: string[] };
  annotationLinks: { save: Record<string, never>; removeIds: string[] };
};

/**
 * 2つのStickyModelContentsのダンプJSONを比較し、各コレクションの差分を計算する。
 *
 * 各コレクションについて:
 * - save: 追加または変更されたアイテム（IDをキーとしたRecord）
 * - removeIds: 削除されたアイテムのID一覧
 *
 * display-order はノード/ゾーンの追加・削除から導出するため、この関数では扱わない。
 * コメントとファイルコンテナは AI の編集結果に現れないため扱わない。線は端点が消えたものの削除だけを扱う。
 */
export function computeModelContentsDiff(
  fromDump: StickyModelContentsJSON,
  toDump: StickyModelContentsJSON
): ModelContentsDiff {
  return {
    nodes: diffArrayItems(fromDump.nodes, toDump.nodes, (n) => extractIdFromKeyString(n.key)),
    links: diffArrayItems(fromDump.links, toDump.links, (l) => extractIdFromKeyString(l.key)),
    zones: diffArrayItems(fromDump.zones, toDump.zones, (z) => z.key),
    zonePositions: diffRecordItems(fromDump.zonePositions, toDump.zonePositions),
    descriptionPanels: diffArrayItems(fromDump.descriptionPanels.panels, toDump.descriptionPanels.panels, (p) => p.id),
    descriptionPanelPositions: diffRecordItems(
      fromDump.descriptionPanels.positions,
      toDump.descriptionPanels.positions
    ),
    nodeDescriptions: diffRecordItems(fromDump.nodeDescriptions, toDump.nodeDescriptions),
    zoneDescriptions: diffRecordItems(fromDump.zoneDescriptions, toDump.zoneDescriptions),
    annotationLinks: { save: {}, removeIds: findDisconnectedAnnotationLinkIds(fromDump, toDump) },
  };
}

/**
 * 端点が消えた線のIDを返す。
 *
 * AI の編集結果は線もファイルコンテナも持たないため、そのまま比較するとすべての線が消えてしまう。
 * 残った要素どうしの線を残すよう、消えた要素につながる線だけを選ぶ。
 */
function findDisconnectedAnnotationLinkIds(
  fromDump: StickyModelContentsJSON,
  toDump: StickyModelContentsJSON
): string[] {
  const targetKeys = new Set([...toDump.nodes, ...toDump.zones].map(({ key }) => key));
  const panelIds = new Set(toDump.descriptionPanels.panels.map(({ id }) => id));

  return fromDump.annotationLinks
    .filter(
      ({ fromKind, fromId, toKey }) =>
        !targetKeys.has(toKey) || (fromKind === 'DescriptionPanel' && !panelIds.has(fromId))
    )
    .map(({ id }) => id);
}

/**
 * "Kind:Id" 形式のキー文字列からId部分を抽出する。
 */
function extractIdFromKeyString(keyString: string): string {
  const colonIndex = keyString.indexOf(':');
  if (colonIndex < 0) return keyString;
  return keyString.substring(colonIndex + 1);
}

/**
 * 配列形式のコレクションを比較し、save（追加/変更）と removeIds（削除）を返す。
 */
function diffArrayItems<T>(
  from: T[],
  to: T[],
  getId: (item: T) => string
): { save: Record<string, T>; removeIds: string[] } {
  const fromMap = new Map(from.map((item) => [getId(item), item]));
  const toMap = new Map(to.map((item) => [getId(item), item]));

  const removeIds: string[] = [];
  for (const [id] of fromMap) {
    if (!toMap.has(id)) {
      removeIds.push(id);
    }
  }

  const save: Record<string, T> = {};
  for (const [id, item] of toMap) {
    const fromItem = fromMap.get(id);
    if (!fromItem || !isEqual(fromItem, item)) {
      save[id] = item;
    }
  }

  return { save, removeIds };
}

/**
 * Record形式のデータを比較し、save（追加/変更）と removeIds（削除）を返す。
 */
function diffRecordItems<V>(
  from: Record<string, V>,
  to: Record<string, V>
): { save: Record<string, V>; removeIds: string[] } {
  const removeIds: string[] = [];
  for (const key of Object.keys(from)) {
    if (!(key in to)) {
      removeIds.push(key);
    }
  }

  const save: Record<string, V> = {};
  for (const [key, value] of Object.entries(to)) {
    if (!isEqual(from[key], value)) {
      save[key] = value;
    }
  }

  return { save, removeIds };
}
